import { useState, useEffect, useRef } from 'react';
import { Heart, RefreshCw, AlertTriangle, CheckCircle, Clock, Server } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { apiClient, SystemHealthResponse } from '../api/client';

export default function SystemHealth() {
  const [health, setHealth] = useState<SystemHealthResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdate, setLastUpdate] = useState<Date | null>(null);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const fetchHealth = async () => {
    try {
      const data = await apiClient.getSystemHealth();
      setHealth(data);
      setLastUpdate(new Date());
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load health status');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchHealth();

    // Refresh every 10 seconds to match health check interval
    intervalRef.current = setInterval(fetchHealth, 10000);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, []);

  const handleRefresh = () => {
    setLoading(true);
    fetchHealth();
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'closed':
        return 'text-green-600 bg-green-100';
      case 'open':
        return 'text-red-600 bg-red-100';
      case 'half_open':
        return 'text-yellow-600 bg-yellow-100';
      default:
        return 'text-gray-600 bg-gray-100';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'closed':
        return <CheckCircle size={24} className="text-green-600" />;
      case 'open':
        return <AlertTriangle size={24} className="text-red-600" />;
      case 'half_open':
        return <Clock size={24} className="text-yellow-600" />;
      default:
        return <Clock size={24} className="text-gray-600" />;
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'closed':
        return 'Healthy';
      case 'open':
        return 'Unhealthy';
      case 'half_open':
        return 'Testing Recovery';
      default:
        return 'Unknown';
    }
  };

  const getHeaderStatusColor = (status: string | undefined) => {
    switch (status) {
      case 'closed':
        return 'bg-green-500';
      case 'open':
        return 'bg-red-500 animate-pulse';
      case 'half_open':
        return 'bg-yellow-500 animate-pulse';
      default:
        return 'bg-gray-400';
    }
  };

  const getHeaderStatusText = (status: string | undefined) => {
    switch (status) {
      case 'closed':
        return 'All Systems Operational';
      case 'open':
        return 'System Issues Detected - Queues Paused';
      case 'half_open':
        return 'Testing API Recovery';
      default:
        return 'Status Unknown';
    }
  };

  if (loading && !health) {
    return (
      <div className="p-8">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-gray-200 rounded w-48" />
          <div className="grid grid-cols-2 gap-4">
            <div className="h-48 bg-gray-200 rounded" />
            <div className="h-48 bg-gray-200 rounded" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-8">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Heart className="text-red-500" />
            System Health
          </h1>
          <div className="flex items-center gap-2">
            <div className={`w-3 h-3 rounded-full ${getHeaderStatusColor(health?.circuitStatus)}`} />
            <span className="text-sm text-gray-500">
              {getHeaderStatusText(health?.circuitStatus)}
            </span>
          </div>
        </div>

        <div className="flex items-center gap-4">
          {lastUpdate && (
            <span className="text-sm text-gray-500">
              Last updated: {formatDistanceToNow(lastUpdate, { addSuffix: true })}
            </span>
          )}
          <button
            onClick={handleRefresh}
            disabled={loading}
            className="flex items-center gap-2 px-4 py-2 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors disabled:opacity-50"
          >
            <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
            Refresh
          </button>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
          <p className="text-red-800">{error}</p>
        </div>
      )}

      {/* Circuit Status Card */}
      <div className="card mb-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-4">
            <div className={`p-4 rounded-full ${getStatusColor(health?.circuitStatus || 'unknown')}`}>
              {getStatusIcon(health?.circuitStatus || 'unknown')}
            </div>
            <div>
              <p className="text-sm text-gray-500 uppercase tracking-wide">Circuit Breaker Status</p>
              <p className="text-3xl font-bold text-gray-900">{getStatusLabel(health?.circuitStatus || 'unknown')}</p>
              {health?.lastStateChange && (
                <p className="text-sm text-gray-500 mt-1">
                  Since {formatDistanceToNow(new Date(health.lastStateChange), { addSuffix: true })}
                </p>
              )}
            </div>
          </div>

          <div className="text-right">
            <p className="text-sm text-gray-500">Consecutive Failures</p>
            <p className={`text-2xl font-bold ${(health?.consecutiveFailures || 0) > 0 ? 'text-red-600' : 'text-green-600'}`}>
              {health?.consecutiveFailures || 0}
            </p>
          </div>
        </div>

        {/* Stats Row */}
        <div className="grid grid-cols-3 gap-4 pt-4 border-t border-gray-100">
          <div className="text-center">
            <p className="text-2xl font-bold text-green-600">{health?.totalSuccesses || 0}</p>
            <p className="text-sm text-gray-500">Total Successes</p>
          </div>
          <div className="text-center">
            <p className="text-2xl font-bold text-red-600">{health?.totalFailures || 0}</p>
            <p className="text-sm text-gray-500">Total Failures</p>
          </div>
          <div className="text-center">
            <p className="text-2xl font-bold text-gray-900">
              {health?.totalSuccesses && health?.totalFailures
                ? `${Math.round((health.totalSuccesses / (health.totalSuccesses + health.totalFailures)) * 100)}%`
                : 'N/A'}
            </p>
            <p className="text-sm text-gray-500">Success Rate</p>
          </div>
        </div>

        {/* Queue Status Banner */}
        {health?.circuitStatus === 'open' && (
          <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-red-800 font-medium">
              ⚠️ Queue Processing Paused — Messages will retry in 30 seconds
            </p>
            {health.openedAt && (
              <p className="text-red-600 text-sm mt-1">
                Circuit opened {formatDistanceToNow(new Date(health.openedAt), { addSuffix: true })}
              </p>
            )}
          </div>
        )}
        {health?.circuitStatus === 'half_open' && (
          <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
            <p className="text-yellow-800 font-medium">
              🔄 Testing API Recovery — Limited queue processing in progress
            </p>
          </div>
        )}
      </div>

      <div className="grid grid-cols-2 gap-6">
        {/* Last Health Check */}
        <div className="card">
          <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <Clock className="text-blue-500" />
            Last Health Check
          </h2>

          {health?.lastCheck ? (
            <div className="space-y-4">
              <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <span className="text-gray-600">Status</span>
                <span className={`font-semibold ${health.lastCheck.healthy ? 'text-green-600' : 'text-red-600'}`}>
                  {health.lastCheck.healthy ? 'Healthy' : 'Unhealthy'}
                </span>
              </div>

              <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <span className="text-gray-600">Latency</span>
                <span className={`font-semibold ${(health.lastCheck.latencyMs || 0) > 2000 ? 'text-yellow-600' : 'text-green-600'}`}>
                  {health.lastCheck.latencyMs}ms
                </span>
              </div>

              {health.lastCheck.statusCode && (
                <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <span className="text-gray-600">HTTP Status</span>
                  <span className={`font-semibold ${health.lastCheck.statusCode >= 200 && health.lastCheck.statusCode < 400 ? 'text-green-600' : 'text-red-600'}`}>
                    {health.lastCheck.statusCode}
                  </span>
                </div>
              )}

              {health.lastCheck.error && (
                <div className="p-3 bg-red-50 border border-red-100 rounded-lg">
                  <p className="text-sm text-red-600 font-medium">Error</p>
                  <p className="text-sm text-red-800 mt-1">{health.lastCheck.error}</p>
                </div>
              )}

              <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <span className="text-gray-600">Checked At</span>
                <span className="text-gray-900">
                  {formatDistanceToNow(new Date(health.lastCheck.timestamp), { addSuffix: true })}
                </span>
              </div>
            </div>
          ) : (
            <div className="text-center py-8">
              <Clock size={48} className="mx-auto text-gray-300 mb-2" />
              <p className="text-gray-500">No health check data available</p>
              <p className="text-sm text-gray-400 mt-1">Health checker may be initializing</p>
            </div>
          )}
        </div>

        {/* Environment Info */}
        <div className="card">
          <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <Server className="text-purple-500" />
            Environment
          </h2>

          <div className="space-y-4">
            <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
              <span className="text-gray-600">Environment</span>
              <span className={`badge ${health?.environment === 'production' ? 'badge-success' : health?.environment === 'staging' ? 'badge-warning' : 'badge-info'}`}>
                {health?.environment || 'unknown'}
              </span>
            </div>

            <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
              <span className="text-gray-600">API Endpoint</span>
              <span className="text-sm text-gray-900 font-mono truncate max-w-xs">
                {health?.locate2uApiUrl || 'N/A'}
              </span>
            </div>

            <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
              <span className="text-gray-600">Health Check Interval</span>
              <span className="text-gray-900">10 seconds</span>
            </div>

            <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
              <span className="text-gray-600">Status Updated</span>
              <span className="text-gray-900">
                {health?.timestamp ? formatDistanceToNow(new Date(health.timestamp), { addSuffix: true }) : 'N/A'}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Circuit Breaker Info */}
      <div className="card mt-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Circuit Breaker Information</h2>
        <div className="prose prose-sm text-gray-600">
          <p>
            The circuit breaker monitors the Locate2u API health every <strong>10 seconds</strong> and automatically
            protects queue processing when the API is down.
          </p>
          <div className="mt-4 space-y-3">
            <div className="flex items-start gap-3">
              <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-green-100 text-green-600 text-xs font-bold shrink-0">✓</span>
              <div>
                <span className="font-semibold text-green-600">CLOSED</span>
                <span className="text-gray-600"> — API healthy, queues process normally</span>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-red-100 text-red-600 text-xs font-bold shrink-0">!</span>
              <div>
                <span className="font-semibold text-red-600">OPEN</span>
                <span className="text-gray-600"> — API down (3+ consecutive failures), queues retry messages with 30s delay</span>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-yellow-100 text-yellow-600 text-xs font-bold shrink-0">↻</span>
              <div>
                <span className="font-semibold text-yellow-600">HALF-OPEN</span>
                <span className="text-gray-600"> — Testing recovery after 30s, limited processing to verify API is back</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Auto-refresh info */}
      <div className="mt-6 text-center text-sm text-gray-500">
        <p>This page refreshes automatically every 10 seconds</p>
      </div>
    </div>
  );
}
